import { Component, EventEmitter, Output } from "@angular/core";
import { MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import { SocialService } from "src/app/service/social.service";
import { SocialReportTargetType } from "src/app/model/social/social.types";

/** Motivos pré-definidos de denúncia. As labels vêm do transloco (`socialFeed.reportReasons.*`). */
export const REPORT_REASON_KEYS = [
  "spam",
  "offensive",
  "nudity",
  "harassment",
  "misinformation",
  "violence",
  "scam",
  "other",
] as const;

/**
 * Diálogo único de denúncia (post, comentário ou conta), usado pelo feed, pelo detalhe do post e
 * pelos perfis de usuário e de projeto. O pai chama `open(targetType, targetId)`; o envio e os
 * toasts ficam aqui, para as quatro telas não repetirem o mesmo fluxo.
 */
@Component({
  selector: "app-social-report-dialog",
  templateUrl: "./social-report-dialog.component.html",
})
export class SocialReportDialogComponent {
  visible = false;
  reasonKey: string | null = null;
  detail = "";
  submitting = false;
  targetType: SocialReportTargetType = "Post";
  targetId: string | null = null;
  readonly reasonKeys: string[] = [...REPORT_REASON_KEYS];

  /** Emitido após a denúncia ser aceita pelo backend. */
  @Output() reported = new EventEmitter<{ targetType: SocialReportTargetType; targetId: string }>();

  constructor(
    private social: SocialService,
    private messageService: MessageService,
    private translocoService: TranslocoService
  ) {}

  /** Chave do título: denúncia de conta tem texto próprio. */
  get headerKey(): string {
    return this.targetType === "User" ? "socialFeed.reportUser" : "socialFeed.report";
  }

  open(targetType: SocialReportTargetType, targetId: string): void {
    this.targetType = targetType;
    this.targetId = targetId;
    this.reasonKey = null;
    this.detail = "";
    this.visible = true;
  }

  submit(): void {
    if (this.submitting) return;
    if (!this.targetId || !this.reasonKey) return;
    const label = this.translocoService.translate("socialFeed.reportReasons." + this.reasonKey);
    const detail = this.detail.trim();
    const reason = detail ? `${label} — ${detail}` : label;
    const targetType = this.targetType;
    const targetId = this.targetId;
    this.submitting = true;
    this.social
      .reportContent({ targetType, targetId, reason, reasonKey: this.reasonKey })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.visible = false;
          this.messageService.add({ severity: "success", summary: this.translocoService.translate("socialFeed.reportSent") });
          this.reported.emit({ targetType, targetId });
        },
        error: (err) => {
          this.submitting = false;
          this.messageService.add({
            severity: "error",
            summary: this.translocoService.translate("socialFeed.reportLabel"),
            detail: SocialService.parseHttpErrorMessage(err),
          });
        },
      });
  }
}
